import { useState, useEffect, useRef } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import configData from "../config.json";

// How far back the recovery query reaches on (re)load. Soft bound; must be <= the
// server retention behind expireAt (FIRESTORE_RETENTION_SECONDS, default 600).
// See REVISED-ARCHITECTURE.md §6.1.
const REPLAY_SECONDS = Number(configData.REALTIME_REPLAY_SECONDS) || 300;

// Firestore message source — the sole realtime path as of M6. Returns
// { messages, setMessages, apiMessages, setApiMessages }. Inert when `enabled` is false
// (no listeners attached, so Firebase is never touched if unconfigured).
export default function useFirestoreMessages({ viewerId, enabled = true }) {
  const [messages, setMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  // Session de-dupe by Firestore doc id (the SDK may re-deliver across reconnects).
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!enabled || !viewerId || viewerId === 0) return;

    seenRef.current = new Set();
    let isFirstSnapshot = true;

    // --- Message listener: recent window, recovery on the initial snapshot. ---
    const cutoff = Timestamp.fromMillis(Date.now() - REPLAY_SECONDS * 1000);
    const messagesQuery = query(
      collection(db, "pupdates", String(viewerId), "messages"),
      where("createdAt", ">", cutoff),
      orderBy("createdAt")
    );

    const unsubMessages = onSnapshot(
      messagesQuery,
      (snapshot) => {
        // First callback = recovery batch → render silently; later = live → play sound.
        const isRecovery = isFirstSnapshot;
        snapshot.docChanges().forEach((change) => {
          // Only act on new docs; ignore `modified` and `removed` (e.g. TTL churn must
          // not pull an on-screen message). Removal is driven by the duration timer.
          if (change.type !== "added") return;

          const docId = change.doc.id;
          if (seenRef.current.has(docId)) return;
          seenRef.current.add(docId);

          const data = change.doc.data() || {};
          // Mirror the socket payload's `content`, tagging recovery so Item can stay silent.
          const content = { ...(data.content || {}), isRecovery };

          // Display lifetime. Parity with the old socket client: a missing/zero
          // duration means immediate removal, never an indefinite message.
          const durationMs = (Number(data.duration) || 0) * 1000;

          // Recovery docs are replayed history: only restore a message that should
          // still be on screen, and only for the time it has left. Otherwise every
          // reload re-shows up to REPLAY_SECONDS of already-expired messages.
          let displayMs = durationMs;
          if (isRecovery) {
            const createdMs = data.createdAt?.toMillis?.() ?? 0;
            displayMs = createdMs + durationMs - Date.now();
            if (displayMs <= 0) return;
          }

          if (data.type === "api") {
            setApiMessages((prev) => [...prev, content]);
          } else {
            setMessages((prev) => [...prev, content]);
            setTimeout(() => {
              setMessages((prev) => prev.filter((m) => m.id !== content.id));
            }, displayMs);
          }
        });
        isFirstSnapshot = false;
      },
      (err) => {
        console.error("Firestore messages listener error:", err);
      }
    );

    // --- Reset listener: reload when the account control doc's resetRequestedAt bumps. ---
    let lastReset = null;
    let firstControlSnapshot = true;
    const unsubControl = onSnapshot(
      doc(db, "pupdates", String(viewerId)),
      (snap) => {
        const ts = snap.data()?.resetRequestedAt?.toMillis?.() ?? null;
        if (firstControlSnapshot) {
          // Baseline on load — don't reload on the initial value.
          lastReset = ts;
          firstControlSnapshot = false;
          return;
        }
        if (ts && ts !== lastReset) {
          lastReset = ts;
          window.location.reload();
        }
      },
      (err) => {
        console.error("Firestore control listener error:", err);
      }
    );

    return () => {
      unsubMessages();
      unsubControl();
    };
  }, [viewerId, enabled]);

  return { messages, setMessages, apiMessages, setApiMessages };
}
